import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SECTIONS, FEE_FREQUENCIES } from "@/lib/sections";

export const emptyMemberForm = {
  full_name: "", date_of_birth: "", section: "scouts", parent_guardian: "", phone: "", email: "",
  address: "", join_date: "", notes: "", medical_info: "", allergies: "", emergency_contact: "",
  emergency_phone: "", uniform_size: "", fee_frequency: "monthly", fee_amount: 20, active: true,
};

// Field descriptors: [key, label, testid, options]
const FIELDS = [
  { key: "full_name", label: "Full name *", testid: "form-full-name", wide: true },
  { key: "section", label: "Section *", testid: "form-section", kind: "select", options: SECTIONS },
  { key: "date_of_birth", label: "Date of birth", testid: "form-dob", type: "date" },
  { key: "parent_guardian", label: "Parent / Guardian", testid: "form-guardian" },
  { key: "phone", label: "Phone", testid: "form-phone" },
  { key: "email", label: "Email", testid: "form-email", type: "email" },
  { key: "join_date", label: "Join date", testid: "form-join-date", type: "date" },
  { key: "address", label: "Address", testid: "form-address", wide: true },
  { key: "fee_frequency", label: "Fee frequency", testid: "form-fee-freq", kind: "select", options: FEE_FREQUENCIES },
  { key: "fee_amount", label: "Fee amount (€)", testid: "form-fee-amt", type: "number", step: "0.01" },
  { key: "uniform_size", label: "Uniform size", testid: "form-uniform" },
  { key: "emergency_contact", label: "Emergency contact", testid: "form-ec" },
  { key: "emergency_phone", label: "Emergency phone", testid: "form-ec-phone" },
  { key: "allergies", label: "Allergies", testid: "form-allergies", wide: true },
  { key: "medical_info", label: "Medical info", testid: "form-medical", kind: "textarea", wide: true },
  { key: "notes", label: "Notes", testid: "form-notes", kind: "textarea", wide: true },
];

function FormField({ f, value, onChange }) {
  if (f.kind === "select") {
    return (
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={f.testid} className="min-h-[44px]"><SelectValue /></SelectTrigger>
        <SelectContent>
          {f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  if (f.kind === "textarea") {
    return <Textarea data-testid={f.testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={2} />;
  }
  return <Input type={f.type || "text"} step={f.step} data-testid={f.testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="min-h-[44px]" />;
}

export default function MemberDialog({ open, onOpenChange, editing, form, setForm, onSave }) {
  const upd = (key) => (value) => setForm({ ...form, [key]: value });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="member-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }}>{editing ? "Edit member" : "Add new member"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {FIELDS.map((f) => (
            <div key={f.key} className={`space-y-1.5 ${f.wide ? "md:col-span-2" : ""}`}>
              <Label>{f.label}</Label>
              <FormField f={f} value={form[f.key]} onChange={upd(f.key)} />
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="member-cancel">Cancel</Button>
          <Button onClick={onSave} data-testid="member-save">{editing ? "Save changes" : "Add member"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
